import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiX } from 'react-icons/fi';
import { useConsent } from '../../context/ConsentContext';
import { isNativeApp } from '../../utils/platform';

// GDPR / ePrivacy-compliant cookie consent panel — compact bottom-sticky
// card (not a full-screen modal). The previous design was a centered
// modal with backdrop blur, which felt like a hard interrupt; this one
// stays out of the way while still being visible enough that the user
// can't miss the consent ask.
//
// One component, two entry paths:
//   1. First-time visitors — `bannerVisible` is true (no decidedAt
//      stamped). Card appears, has no close affordance: the user must
//      pick one of "Accept total" / "Refuz total" / "Salvează-mi
//      alegerile" before the choice is recorded. The law treats
//      passive consent (e.g. clicking through the page) as no consent,
//      and our backing state stays at the denied default until the
//      user clicks, regardless of whether they ignore the card.
//   2. Returning visitors via the Footer "Setări cookie-uri" link —
//      `settingsOpen` flips on through openSettings(). Card can be
//      dismissed via the X button (previously-stored decision stays).
//
// All three CTAs are equal-width and use the same height/padding so the
// "Accept = Reject" parity required by GDPR holds. The brand-orange
// Accept button is the only color signal — no size/font hierarchy
// nudging users towards it.
//
// Categories follow the standard EU template:
//   • Necesar         — locked checked, can't be disabled
//   • Personalizare   — off by default
//   • Marketing       — off by default
//   • Analize         — off by default; gates GA4 + Sentry
export default function CookieBanner() {
  const {
    bannerVisible,
    settingsOpen,
    analytics: savedAnalytics,
    marketing: savedMarketing,
    personalization: savedPersonalization,
    acceptAll,
    rejectOptional,
    savePreferences,
    closeSettings,
  } = useConsent();

  const isOpen = bannerVisible || settingsOpen;
  const canDismiss = !bannerVisible && settingsOpen;

  const [analytics, setAnalytics] = useState(savedAnalytics);
  const [marketing, setMarketing] = useState(savedMarketing);
  const [personalization, setPersonalization] = useState(savedPersonalization);

  // Re-seed the form whenever the panel opens, so reopening it from
  // the footer reflects the currently-saved preferences (the user may
  // have pressed "Accept total" elsewhere since the last open).
  useEffect(() => {
    if (isOpen) {
      setAnalytics(savedAnalytics);
      setMarketing(savedMarketing);
      setPersonalization(savedPersonalization);
    }
  }, [isOpen, savedAnalytics, savedMarketing, savedPersonalization]);

  // Native app: this banner is web-only. GA4 / Meta Pixel are hard-disabled
  // in the binary (App Store 5.1.2 / ATT); crash/diagnostics (Sentry) runs
  // on legitimate interest with NO first-launch prompt (matches near-every
  // consumer app's pattern — Instagram, Uber, etc. don't gate crash
  // reporting behind a consent screen either), auto-granted by
  // ConsentContext. There is NO in-app diagnostics toggle (matches
  // Mero/Fresha); the Art. 21 opt-out route is a request to
  // privacy@petzy.ro, as documented on the /gdpr page.
  if (isNativeApp) return null;
  if (!isOpen) return null;

  const handleSave = () => {
    savePreferences({ analytics, marketing, personalization });
  };

  return (
    // Outer wrapper is pointer-events-none so it doesn't intercept clicks
    // on the page below — only the card itself is interactive. This keeps
    // the rest of the site usable while consent is pending, which the law
    // explicitly allows (passive browsing ≠ consent; the user's recorded
    // decision is still 'denied' until they click a CTA).
    // role="dialog" + aria-labelledby live on the INNER card (the
    // pointer-events-auto element), not on this transparent positioning
    // wrapper. Screen readers should associate the dialog role with the
    // actual interactive surface; placing them on the wrapper would
    // announce a dialog that the user can't focus or interact with.
    // pb includes env(safe-area-inset-bottom): the native app runs
    // edge-to-edge (viewport-fit=cover), so without it the CTA row sits
    // under the iOS home indicator / Android gesture bar.
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] sm:pb-[calc(env(safe-area-inset-bottom,0px)+1rem)] pointer-events-none">
      <div
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-consent-title"
        aria-describedby="cookie-consent-description"
        className="mx-auto max-w-4xl pointer-events-auto bg-white rounded-2xl shadow-2xl border border-gray-200"
      >
        <div className="px-4 py-3.5 sm:px-5 sm:py-4">
          <div className="flex items-start justify-between gap-3 mb-2.5">
            <div className="flex-1 min-w-0">
              <h3
                id="cookie-consent-title"
                className="text-sm sm:text-base font-semibold text-gray-900 mb-1"
              >
                Cookie-uri pe Petzy
              </h3>
              <p
                id="cookie-consent-description"
                className="text-xs sm:text-sm text-gray-600 leading-relaxed"
              >
                Folosim cookie-uri pentru a face platforma să funcționeze și, cu acordul tău, pentru analiză, marketing și personalizare. Detalii în{' '}
                <Link
                  to="/cookies"
                  className="text-orange-600 underline hover:text-orange-700"
                  onClick={canDismiss ? closeSettings : undefined}
                >
                  politica de cookie-uri
                </Link>.
              </p>
            </div>
            {canDismiss && (
              // p-2 with -m-2 keeps the close button's visual size the
              // same as the rest of the inline header (icon stays at
              // text-lg) but expands the actual touch target to ~36px
              // — close to the 44-48px floor recommended by WCAG 2.5.5
              // for mobile. The negative margin pulls the expanded hit
              // box back inside the card padding so the visual layout
              // doesn't shift. focus-visible ring lines up with the
              // Toggle component's focus treatment so keyboard users
              // see a consistent indicator across the panel; the FiX
              // icon is aria-hidden because the surrounding button's
              // aria-label already announces the action.
              <button
                type="button"
                onClick={closeSettings}
                className="flex-shrink-0 p-2 -m-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-600 focus-visible:ring-offset-1"
                aria-label="Închide"
              >
                <FiX className="text-lg" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Toggle row — inline switches keep the category UI compact
              enough for a bottom-bar layout while still letting users
              pick categories without opening a separate settings modal. */}
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2.5 py-3 mb-3 border-y border-gray-100">
            <Toggle id="cookie-necessary" label="Necesar" checked disabled />
            <Toggle
              id="cookie-personalization"
              label="Personalizare"
              checked={personalization}
              onChange={setPersonalization}
            />
            <Toggle
              id="cookie-marketing"
              label="Marketing"
              checked={marketing}
              onChange={setMarketing}
            />
            <Toggle
              id="cookie-analytics"
              label="Analize"
              checked={analytics}
              onChange={setAnalytics}
            />
          </div>

          {/* Equal-width grid keeps the three CTAs at parity per GDPR
              "no nudging" guidance: the user shouldn't have to look
              harder for Reject than for Accept. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={rejectOptional}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Refuz total
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Salvează alegerile
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors"
            >
              Accept total
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Modern switch toggle. Uses a hidden checkbox + Tailwind `peer-*`
// utilities so the visible track / thumb stay in sync with the input's
// checked / disabled state without extra JS. Keeps native keyboard
// support and screen-reader announcement.
function Toggle({ id, label, checked, onChange, disabled }) {
  return (
    <label
      htmlFor={id}
      className={`inline-flex items-center gap-2 ${
        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
      }`}
    >
      <span className="relative inline-flex items-center">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange && onChange(e.target.checked)}
          className="peer sr-only"
        />
        <span className="block h-5 w-9 rounded-full bg-gray-300 transition-colors peer-checked:bg-orange-500 peer-disabled:opacity-60 peer-focus-visible:ring-2 peer-focus-visible:ring-orange-600 peer-focus-visible:ring-offset-1" />
        <span className="pointer-events-none absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
      </span>
      <span
        className={`text-xs sm:text-sm font-medium ${
          disabled ? 'text-gray-500' : 'text-gray-800'
        }`}
      >
        {label}
      </span>
    </label>
  );
}
